import { MapPin, ShieldCheck, Clock, Bike, ArrowRight, ArrowUpRight } from "lucide-react";
import { Reveal } from "@/components/Reveal";

const features = [
  { icon: ShieldCheck, label: "Dubai Municipality compliant" },
  { icon: Clock, label: "24/7 secure access" },
  { icon: Bike, label: "Talabat · Deliveroo · Noon ready" },
];

const MAPS_URL = "https://maps.google.com/?q=Dubai+Investment+Park+Phase+2,+Dubai,+UAE";

export default function Kitchens() {
  return (
    <section id="kitchens" className="py-24 md:py-32" data-testid="kitchens-section">
      <div className="mx-auto max-w-7xl px-5 md:px-8">
        <Reveal className="mb-14 flex flex-wrap items-end justify-between gap-6">
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-[0.34em] text-gold" data-testid="kitchens-eyebrow">Our Facility</p>
            <h2 className="mt-4 font-serif text-4xl font-medium tracking-tight text-white md:text-5xl" data-testid="kitchens-heading">
              One flagship kitchen. <span className="italic text-gold">Built to a royal standard.</span>
            </h2>
          </div>
          <p className="max-w-sm text-sm font-light leading-relaxed text-white/60">
            Royal Express operates a single purpose-built kitchen units center in Dubai Investment Park — every unit licensed, equipped and ready to cook.
          </p>
        </Reveal>

        <div className="grid gap-5 md:grid-cols-3">
          <Reveal className="group md:col-span-2">
            <a
              href={MAPS_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="facility-tile"
              className="relative block h-80 overflow-hidden rounded-2xl border border-gold/15 transition-colors duration-500 hover:border-gold/40 md:h-96"
            >
              <img
                src="/assets/gallery/equipment-lineup.webp"
                alt="Royal Express flagship cloud kitchen equipment lineup, Dubai Investment Park"
                loading="lazy"
                className="h-full w-full object-cover transition-transform duration-[1400ms] ease-out group-hover:scale-110"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/25 to-transparent" aria-hidden="true" />
              <span className="absolute right-4 top-4 rounded-full border border-gold/40 bg-black/60 px-3 py-1 text-[9px] font-semibold uppercase tracking-[0.2em] text-gold backdrop-blur-md">
                Flagship Facility
              </span>
              <div className="absolute inset-x-0 bottom-0 flex items-end justify-between p-6">
                <div>
                  <p className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.26em] text-gold"><MapPin size={11} /> Dubai, UAE</p>
                  <h3 className="mt-2 font-serif text-2xl font-medium text-white md:text-3xl">Dubai Investment Park</h3>
                </div>
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-gold/50 text-gold transition-all duration-500 group-hover:bg-gold group-hover:text-black">
                  <ArrowRight size={16} />
                </span>
              </div>
            </a>
          </Reveal>

          <Reveal delay={0.12}>
            <a
              href={MAPS_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="coverage-tile"
              className="group relative flex h-80 flex-col justify-between overflow-hidden rounded-2xl border border-gold/25 bg-forest-deep p-7 transition-colors duration-500 hover:border-gold/50 md:h-96"
            >
              <div>
                <p className="text-[10px] font-semibold uppercase tracking-[0.3em] text-gold">Coverage Area</p>
                <h3 className="mt-3 font-serif text-2xl font-medium leading-snug text-white md:text-3xl">
                  From DIP to <span className="italic text-gold">every doorstep.</span>
                </h3>
                <p className="mt-4 text-sm font-light leading-relaxed text-white/65" data-testid="coverage-copy">
                  Delivery coverage across Dubai South, Al Furjan, the Green Community in DIP, the developing Jebel Ali Village, and the Jumeirah Beach Residence area.
                </p>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-[10px] uppercase tracking-[0.26em] text-white/50">Riders reach millions</span>
                <span className="flex h-10 w-10 items-center justify-center rounded-full border border-gold/50 text-gold transition-all duration-500 group-hover:bg-gold group-hover:text-black">
                  <ArrowRight size={16} />
                </span>
              </div>
            </a>
          </Reveal>
        </div>

        <div className="mt-10 grid gap-6 lg:grid-cols-2">
          <Reveal className="rounded-2xl border border-gold/25 bg-forest-deep/60 p-7 md:p-9" data-testid="facility-address-card">
            <p className="flex items-center gap-2 text-[10px] font-semibold uppercase tracking-[0.3em] text-gold">
              <MapPin size={12} /> Flagship Address
            </p>
            <p className="mt-4 font-serif text-xl font-medium leading-relaxed text-white md:text-2xl" data-testid="facility-address">
              Royal Express Kitchen Units Center, Dubai Investment Park, Phase 2, Plot 597–775, Off Expo 2020 Road, Near Civil Defence Roundabout, P.O. Box 24381, Dubai, UAE
            </p>
            <div className="mt-7 flex flex-wrap gap-3" data-testid="facility-feature-chips">
              {features.map((f) => (
                <span key={f.label} className="flex items-center gap-2 rounded-full border border-gold/25 bg-gold/[0.06] px-4 py-2 text-[11px] tracking-wide text-white/80">
                  <f.icon size={13} className="text-gold" /> {f.label}
                </span>
              ))}
            </div>
            <a
              href={MAPS_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="facility-directions-link"
              className="mt-7 inline-flex items-center gap-2 rounded-full border border-gold/50 px-6 py-3 text-[11px] font-semibold uppercase tracking-[0.2em] text-gold transition-colors duration-300 hover:bg-gold/10"
            >
              Get Directions <ArrowUpRight size={14} />
            </a>
          </Reveal>
          <Reveal delay={0.12} className="relative min-h-[320px] overflow-hidden rounded-2xl border border-gold/20">
            <iframe
              title="Royal Express Kitchen Units Center — Dubai Investment Park Phase 2 map"
              src="https://www.google.com/maps?q=Dubai+Investment+Park+Phase+2+Dubai&output=embed"
              className="absolute inset-0 h-full w-full"
              loading="lazy"
              data-testid="facility-map-embed"
            />
          </Reveal>
        </div>
      </div>
    </section>
  );
}
